/**
 * CCTV bullet-camera glyph, drawn once at runtime into a 64×64 canvas and
 * served to deck.gl's IconLayer as a data-URL atlas.
 *
 * Why runtime-drawn instead of a checked-in PNG: the pictogram is ~10 lines
 * of canvas, stays crisp at any pixel ratio, and inherits the no-new-binary-
 * asset rule the map layers follow. The glyph itself is pure white with
 * transparency — IconLayer's getColor tints it per camera (green = online,
 * red = down, grey = unknown), so a single atlas entry serves every state.
 */

const SIZE = 64;

let cached: string | null = null;

/** Side-view bullet camera angled down-right: ceiling mount, body, lens. */
function draw(ctx: CanvasRenderingContext2D): void {
  ctx.clearRect(0, 0, SIZE, SIZE);
  // axiom-audit-ignore-next-line colors -- SVG canvas paint; pure #fff here is the camera body's instrument-face highlight, not a UI surface.
  ctx.fillStyle = "#ffffff";

  // Ceiling mount: base plate + drop arm.
  ctx.fillRect(26, 3, 12, 5);
  ctx.fillRect(29, 8, 6, 12);

  // Body: bullet housing angled ~25° down toward the right.
  ctx.save();
  ctx.translate(32, 36);
  ctx.rotate(Math.PI / 7);
  const bw = 40;
  const bh = 15;
  ctx.beginPath();
  ctx.roundRect(-bw / 2, -bh / 2, bw, bh, 7);
  ctx.fill();
  // Sunshield lip at the front (right end).
  ctx.fillRect(bw / 2 - 4, -bh / 2 - 3, 5, 3);
  ctx.restore();

  // Lens: punch a transparent hole at the front, keep a thin white rim so
  // the glass reads even over a bright basemap.
  ctx.save();
  ctx.globalCompositeOperation = "destination-out";
  ctx.beginPath();
  ctx.arc(47, 44, 5.5, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
  ctx.save();
  // axiom-audit-ignore-next-line colors -- SVG canvas paint; pure #fff here is the lens ring stroke, not a UI surface.
  ctx.strokeStyle = "#ffffff";
  ctx.lineWidth = 2.5;
  ctx.beginPath();
  ctx.arc(47, 44, 6.5, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
}

/** Data-URL atlas for the cctv-cameras IconLayer. Empty string off-DOM. */
export function cameraIconAtlas(): string {
  if (cached) return cached;
  if (typeof document === "undefined") return "";
  const canvas = document.createElement("canvas");
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext("2d");
  if (!ctx) return "";
  draw(ctx);
  cached = canvas.toDataURL();
  return cached;
}

export const CAMERA_ICON_MAPPING = {
  cam: { x: 0, y: 0, width: SIZE, height: SIZE },
} as const;
